import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { Button } from 'components/index';
import { toast } from 'react-toastify';
import { ProjectLineItemDTO, ProjectLineItemSubtotalDTO, SubtotalReanchorDTO } from './project.type';
import { createDrawLineItemSubtotal, updateDrawLineItemSubtotal, deleteDrawLineItemSubtotal } from './project.api';
import { computeRangePool, getDefaultCheckedIds, toggleRangeItem } from './subtotalRangeEdit.util';

// Shared subtotal support for any grid that renders a draw's line items (DrawLineItemsTable.tsx —
// used by the payapp merge workflow — and ProjectTable.tsx — the plain project page). Extracted
// here because none of this logic depends on fuzzy-match/merge concepts (matchType, payappRow,
// projectRow, groupId) that only DrawLineItemsTable.tsx needs, so it can be shared as-is.

// Builds a synthetic ProjectLineItemDTO-shaped row from a subtotal DTO so it can be rendered
// through the existing line item columns with minimal special-casing. `displaySerialNumber`
// should be the anchor line item's *current* serialNumber (falling back to the subtotal's
// last-known anchorSerialNumber if the anchor can no longer be found), so the subtotal keeps
// following its anchor through reordering without needing any changes to reorder logic.
export const buildSubtotalDisplayRow = (
  s: ProjectLineItemSubtotalDTO,
  drawId: string,
  drawName: string,
  displaySerialNumber: number,
): ProjectLineItemDTO => ({
  id: s.id,
  serialNumber: displaySerialNumber,
  originalBudget: s.originalBudget,
  changeOrder: s.changeOrder,
  reallocation: s.reallocation,
  revisedBudget: s.revisedBudget,
  previousApplication: s.previousApplication,
  completeThisPeriod: s.completeThisPeriod,
  storedThisPeriod: s.storedThisPeriod,
  cdccApproved: s.cdccApproved,
  completedStoredToDate: s.completedStoredToDate,
  percentageComplete: s.percentageComplete,
  balanceToComplete: s.balanceToComplete,
  retainage: s.retainage,
  quantity: s.quantity,
  lineItem: { id: '', description: s.label, unit: '', division: undefined } as any,
  projectDrawId: drawId,
  projectDrawName: drawName,
  isIncluded: false,
  __rowType: 'subtotal',
  __subtotalId: s.id,
});

// Interleaves subtotal rows among the filtered line items, sorted by position. A subtotal renders
// immediately after the line item it's anchored to (using that anchor's *current* serialNumber,
// so subtotals stay correctly placed across reordering without any change to reorder logic).
//
// Always computes a subtotal's displayed values live from the current line items, rather than
// trusting whatever numbers happen to be stored on the DTO. This keeps values correct both for
// backend-fetched project-table subtotals (which auto-update as rows are edited, with no refetch
// needed) and for payapp-merge subtotals (Step 3/4), which arrive with placeholder zeros since
// there's no backend round-trip yet to compute them.
//
// Deliberately sums over `filteredLineItems` (all non-deleted line items), not `displayData`'s own
// `filteredData` input (which may reflect the caller's active search/grid filters) — a subtotal's
// value must not change just because the grid is currently filtered.
export const computeSubtotalDisplayData = (
  filteredData: ProjectLineItemDTO[],
  filteredLineItems: ProjectLineItemDTO[],
  subtotals: ProjectLineItemSubtotalDTO[],
  drawId: string,
  drawName: string,
): ProjectLineItemDTO[] => {
  const activeSubtotals = subtotals.filter(s => !s.isDeleted);
  if (activeSubtotals.length === 0) return filteredData;

  const currentSerialByLineItemId = new Map(filteredLineItems.map(li => [li.id, li.serialNumber ?? 0]));

  const orderedSubtotals = [...activeSubtotals].sort((a, b) => {
    const serialA = currentSerialByLineItemId.get(a.anchorDrawLineItemId) ?? a.anchorSerialNumber;
    const serialB = currentSerialByLineItemId.get(b.anchorDrawLineItemId) ?? b.anchorSerialNumber;
    return serialA - serialB;
  });

  const sortedLineItems = [...filteredLineItems].sort((a, b) => (a.serialNumber ?? 0) - (b.serialNumber ?? 0));

  let previousAnchorSerial = -Infinity;
  const subtotalRows: ProjectLineItemDTO[] = orderedSubtotals.map(s => {
    const anchorSerial = currentSerialByLineItemId.get(s.anchorDrawLineItemId) ?? s.anchorSerialNumber;
    let rangeItems = sortedLineItems.filter(li => (li.serialNumber ?? 0) > previousAnchorSerial && (li.serialNumber ?? 0) <= anchorSerial);
    previousAnchorSerial = anchorSerial;

    // An explicit range takes the last N items of the default range (by serial number), which
    // naturally clamps to the previous subtotal's anchor / top of table for free, since rangeItems
    // is already bounded by that default range.
    if (s.range != null && s.range < rangeItems.length) {
      rangeItems = rangeItems.slice(rangeItems.length - s.range);
    }

    const sum = (selector: (li: ProjectLineItemDTO) => number | undefined) => rangeItems.reduce((acc, li) => acc + (selector(li) || 0), 0);

    const revisedBudget = sum(li => li.revisedBudget);
    const completedStoredToDate = sum(li => li.completedStoredToDate);

    const computed: ProjectLineItemSubtotalDTO = {
      ...s,
      originalBudget: sum(li => li.originalBudget),
      changeOrder: sum(li => li.changeOrder),
      reallocation: sum(li => li.reallocation),
      revisedBudget,
      previousApplication: sum(li => li.previousApplication),
      completeThisPeriod: sum(li => li.completeThisPeriod),
      storedThisPeriod: sum(li => li.storedThisPeriod),
      cdccApproved: sum(li => li.cdccApproved),
      completedStoredToDate,
      percentageComplete: revisedBudget !== 0 ? (completedStoredToDate / revisedBudget) * 100 : 0,
      balanceToComplete: sum(li => li.balanceToComplete),
      retainage: sum(li => li.retainage),
      quantity: sum(li => li.quantity),
    };

    return buildSubtotalDisplayRow(computed, drawId, drawName, anchorSerial);
  });

  return [...filteredData, ...subtotalRows].sort((a, b) => {
    const aKey = a.serialNumber ?? 0;
    const bKey = b.serialNumber ?? 0;
    if (aKey !== bKey) return aKey - bKey;
    // Same serial number: the real line item sorts before a subtotal anchored to it.
    const aIsSubtotal = a.__rowType === 'subtotal';
    const bIsSubtotal = b.__rowType === 'subtotal';
    if (aIsSubtotal === bIsSubtotal) return 0;
    return aIsSubtotal ? 1 : -1;
  });
};

// Given the set of line item ids being deleted together (one for a single delete, several for a
// bulk delete), computes the updated subtotals list (re-anchored to the nearest surviving row
// above, or dropped entirely if none remains) plus the reanchor instructions to forward to the
// backend on save.
export const computeSubtotalReanchors = (
  deletedIds: Set<string>,
  currentSubtotals: ProjectLineItemSubtotalDTO[],
  allLineItems: ProjectLineItemDTO[],
): { updatedSubtotals: ProjectLineItemSubtotalDTO[]; reanchors: SubtotalReanchorDTO[] } => {
  const affected = currentSubtotals.filter(s => deletedIds.has(s.anchorDrawLineItemId));
  if (affected.length === 0) {
    return { updatedSubtotals: currentSubtotals, reanchors: [] };
  }

  const lineItemsById = new Map(allLineItems.map(li => [li.id, li]));

  // Rows that will still be present after this delete completes, sorted by position.
  const survivors = allLineItems
    .filter(li => !li.isDeleted && !deletedIds.has(li.id))
    .sort((a, b) => (a.serialNumber ?? 0) - (b.serialNumber ?? 0));

  const updatedSubtotals: ProjectLineItemSubtotalDTO[] = [];
  const reanchors: SubtotalReanchorDTO[] = [];

  for (const subtotal of currentSubtotals) {
    if (!deletedIds.has(subtotal.anchorDrawLineItemId)) {
      updatedSubtotals.push(subtotal);
      continue;
    }

    const deletedAnchor = lineItemsById.get(subtotal.anchorDrawLineItemId);
    const deletedAnchorSerialNumber = deletedAnchor?.serialNumber ?? 0;

    // Nearest surviving row above the deleted anchor's original position.
    let nearestAbove: ProjectLineItemDTO | undefined;
    for (const li of survivors) {
      if ((li.serialNumber ?? 0) < deletedAnchorSerialNumber) {
        nearestAbove = li;
      } else {
        break;
      }
    }

    const newAnchorDrawLineItemId = nearestAbove?.id ?? null;
    reanchors.push({ subtotalId: subtotal.id, newAnchorDrawLineItemId });

    if (newAnchorDrawLineItemId && nearestAbove) {
      updatedSubtotals.push({
        ...subtotal,
        anchorDrawLineItemId: newAnchorDrawLineItemId,
        anchorSerialNumber: nearestAbove.serialNumber ?? subtotal.anchorSerialNumber,
      });
    }
    // else: no row remains above it — the subtotal is dropped, matching backend behavior.
  }

  return { updatedSubtotals, reanchors };
};

interface SubtotalLabelDialogProps {
  open: boolean;
  title: string;
  initialLabel: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (label: string) => void;
}

// Label-only now — the range (which line items a subtotal sums) is set interactively via
// checkboxes in the grid itself (see useSubtotalRangeEditor below), not through this dialog.
export function SubtotalLabelDialog({ open, title, initialLabel, saving, onClose, onSubmit }: SubtotalLabelDialogProps) {
  const [label, setLabel] = React.useState(initialLabel);

  React.useEffect(() => {
    if (open) setLabel(initialLabel);
  }, [open, initialLabel]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth margin='dense' label='Label' value={label} onChange={e => setLabel(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='secondary' disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(label.trim())} disabled={!label.trim() || saving} variant='contained' color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SubtotalLabelDialog.displayName = 'SubtotalLabelDialog';

type SubtotalDialogState = { mode: 'create'; anchorLineItemId: string } | { mode: 'edit'; subtotal: ProjectLineItemSubtotalDTO } | null;

// Bundles subtotal create/edit-label/delete actions behind a small dialog, so any grid can wire
// up "Insert Subtotal Below" / "Edit Subtotal Label" / "Delete Subtotal" without re-implementing
// the API calls or dialog state. Each consumer supplies `onSubtotalsChange` to fold the resulting
// subtotals array into its own state shape (a single `projectDraw` object vs. a `detailedDraws`
// list + separate update callback, etc.).
export function useSubtotalActions({
  drawId,
  subtotals,
  onSubtotalsChange,
  getConfirmation,
}: {
  drawId: string;
  subtotals: ProjectLineItemSubtotalDTO[];
  onSubtotalsChange: (next: ProjectLineItemSubtotalDTO[]) => void;
  getConfirmation: (params: any) => Promise<boolean>;
}) {
  const [subtotalDialogState, setSubtotalDialogState] = React.useState<SubtotalDialogState>(null);
  const [subtotalDialogSaving, setSubtotalDialogSaving] = React.useState(false);

  const handleInsertSubtotalClick = (row: ProjectLineItemDTO) => {
    setSubtotalDialogState({ mode: 'create', anchorLineItemId: row.id });
  };

  const handleEditSubtotalLabelClick = (subtotalRowId: string) => {
    const subtotal = subtotals.find(s => s.id === subtotalRowId);
    if (!subtotal) {
      toast.error('Unable to find this subtotal. Please try again.');
      return;
    }
    setSubtotalDialogState({ mode: 'edit', subtotal });
  };

  const handleDeleteSubtotalClick = async (subtotalRowId: string) => {
    const subtotal = subtotals.find(s => s.id === subtotalRowId);
    if (!subtotal) {
      toast.error('Unable to find this subtotal. Please try again.');
      return;
    }

    try {
      const isConfirmed = await getConfirmation({
        heading: `Delete Subtotal "${subtotal.label}"`,
        description: 'Are you sure you want to delete this subtotal? This does not affect the line items it summarizes.',
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      await deleteDrawLineItemSubtotal(subtotal.id);

      onSubtotalsChange(subtotals.filter(s => s.id !== subtotal.id));
      toast.success('Subtotal deleted successfully.');
    } catch (error) {
      console.error('Error deleting subtotal:', error);
      toast.error('An error occurred while deleting the subtotal.');
    }
  };

  const handleSubtotalDialogClose = () => {
    if (subtotalDialogSaving) return;
    setSubtotalDialogState(null);
  };

  const handleSubtotalDialogSubmit = async (label: string) => {
    if (!subtotalDialogState || !label) return;

    setSubtotalDialogSaving(true);
    try {
      if (subtotalDialogState.mode === 'create') {
        const created = await createDrawLineItemSubtotal(drawId, subtotalDialogState.anchorLineItemId, label);
        onSubtotalsChange([...subtotals, created]);
        toast.success('Subtotal added successfully.');
      } else {
        const updated = await updateDrawLineItemSubtotal(subtotalDialogState.subtotal.id, { label });
        onSubtotalsChange(subtotals.map(s => (s.id === updated.id ? updated : s)));
        toast.success('Subtotal updated successfully.');
      }
      setSubtotalDialogState(null);
    } catch (error) {
      console.error('Error saving subtotal:', error);
      toast.error('An error occurred while saving the subtotal.');
    } finally {
      setSubtotalDialogSaving(false);
    }
  };

  return {
    subtotalDialogState,
    subtotalDialogSaving,
    handleInsertSubtotalClick,
    handleEditSubtotalLabelClick,
    handleDeleteSubtotalClick,
    handleSubtotalDialogClose,
    handleSubtotalDialogSubmit,
  };
}

export interface SubtotalRangeApplyResult {
  subtotalId: string;
  anchorDrawLineItemId: string;
  anchorSerialNumber: number;
  range: number;
  // The draw's full line item list (including deleted, in their original slots), with
  // serialNumber recomputed for every active item to reflect the new order — same convention as
  // DrawLineItemsTable.tsx's drag-and-drop `moveRow`.
  reorderedLineItems: ProjectLineItemDTO[];
}

// Drives the checkbox-based subtotal range editor shared by ProjectTable.tsx and
// DrawLineItemsTable.tsx. While a subtotal is "in range edit mode", checkboxes appear on every
// line item in its editable pool (everything strictly between the previous subtotal's anchor and
// the next subtotal's anchor — the only rows it could ever claim). Checking/unchecking a row
// reorders the underlying line items per the rules in subtotalRangeEdit.util.ts, so that a
// subtotal's range always stays representable as "N rows counting back from the anchor."
export function useSubtotalRangeEditor({
  lineItems,
  subtotals,
  onApply,
}: {
  lineItems: ProjectLineItemDTO[];
  subtotals: ProjectLineItemSubtotalDTO[];
  onApply: (result: SubtotalRangeApplyResult) => void;
}) {
  const [rangeEditSubtotalId, setRangeEditSubtotalId] = React.useState<string | null>(null);
  const [poolIds, setPoolIds] = React.useState<string[]>([]);
  const [originalPoolIds, setOriginalPoolIds] = React.useState<string[]>([]);
  const [poolStartIndex, setPoolStartIndex] = React.useState(0);
  const [anchorId, setAnchorId] = React.useState<string>('');
  const [checkedIds, setCheckedIds] = React.useState<Set<string>>(new Set());

  const activeItems = React.useMemo(() => lineItems.filter(li => !li.isDeleted), [lineItems]);
  const orderedActiveItems = React.useMemo(
    () => [...activeItems].sort((a, b) => (a.serialNumber ?? 0) - (b.serialNumber ?? 0)),
    [activeItems],
  );
  const orderedIds = React.useMemo(() => orderedActiveItems.map(li => li.id), [orderedActiveItems]);
  const serialByLineItemId = React.useMemo(
    () => new Map(orderedActiveItems.map(li => [li.id, li.serialNumber ?? 0])),
    [orderedActiveItems],
  );

  const sortedSubtotals = React.useMemo(
    () =>
      [...subtotals]
        .filter(s => !s.isDeleted)
        .sort(
          (a, b) =>
            (serialByLineItemId.get(a.anchorDrawLineItemId) ?? a.anchorSerialNumber) -
            (serialByLineItemId.get(b.anchorDrawLineItemId) ?? b.anchorSerialNumber),
        ),
    [subtotals, serialByLineItemId],
  );

  const startRangeEdit = (subtotalId: string) => {
    const subtotal = subtotals.find(s => s.id === subtotalId);
    if (!subtotal) return;

    const idx = sortedSubtotals.findIndex(s => s.id === subtotalId);
    const previousAnchorId = idx > 0 ? sortedSubtotals[idx - 1].anchorDrawLineItemId : null;
    const nextAnchorId = idx >= 0 && idx < sortedSubtotals.length - 1 ? sortedSubtotals[idx + 1].anchorDrawLineItemId : null;

    const startIdx = previousAnchorId ? orderedIds.indexOf(previousAnchorId) + 1 : 0;
    const pool = computeRangePool(orderedIds, previousAnchorId, nextAnchorId);
    const checked = getDefaultCheckedIds(pool, subtotal.anchorDrawLineItemId, subtotal.range);

    setRangeEditSubtotalId(subtotalId);
    setPoolIds(pool);
    setOriginalPoolIds(pool);
    setPoolStartIndex(Math.max(0, startIdx));
    setAnchorId(subtotal.anchorDrawLineItemId);
    setCheckedIds(checked);
  };

  const toggleItem = (itemId: string, checked: boolean) => {
    const result = toggleRangeItem(poolIds, originalPoolIds, anchorId, checkedIds, itemId, checked);
    if (!result) {
      toast.error(checked ? 'Unable to include this line item in the subtotal.' : 'A subtotal must include at least one line item.');
      return;
    }
    setPoolIds(result.poolIds);
    setAnchorId(result.anchorId);
    setCheckedIds(result.checkedIds);
  };

  const cancelRangeEdit = () => {
    setRangeEditSubtotalId(null);
    setPoolIds([]);
    setOriginalPoolIds([]);
    setCheckedIds(new Set());
    setAnchorId('');
  };

  // Shared by the live preview (below) and confirmRangeEdit — reorders the pool's slice of
  // orderedIds into the current in-progress order and stamps serialNumber = index+1 across all
  // active items, same convention as DrawLineItemsTable.tsx's drag-and-drop `moveRow`.
  const computeReorderedLineItems = (markEdited: boolean) => {
    const nextOrderedIds = orderedIds.slice();
    nextOrderedIds.splice(poolStartIndex, poolIds.length, ...poolIds);

    const activeById = new Map(activeItems.map(li => [li.id, li]));
    const nextActiveOrder = nextOrderedIds.map(id => activeById.get(id)).filter((li): li is ProjectLineItemDTO => !!li);

    const nextActiveWithSerials = nextActiveOrder.map((item, index) => ({
      ...item,
      serialNumber: index + 1,
      isEdited: markEdited ? (!item.isCreated ? true : item.isEdited) : item.isEdited,
    }));

    let i = 0;
    const reorderedLineItems = lineItems.map(li => (li.isDeleted ? li : nextActiveWithSerials[i++]));
    const newAnchorSerialNumber = nextActiveWithSerials.find(li => li.id === anchorId)?.serialNumber ?? 0;

    return { reorderedLineItems, newAnchorSerialNumber };
  };

  // Live preview of the reorder-in-progress, for the grid to render immediately as the user
  // checks/unchecks rows, rather than only after Confirm. Null when no subtotal is being edited.
  const previewLineItems = React.useMemo(() => {
    if (!rangeEditSubtotalId) return null;
    return computeReorderedLineItems(false).reorderedLineItems;
  }, [rangeEditSubtotalId, orderedIds, poolStartIndex, poolIds, activeItems, lineItems, anchorId]);

  const previewSubtotals = React.useMemo(() => {
    if (!rangeEditSubtotalId) return null;
    const { newAnchorSerialNumber } = computeReorderedLineItems(false);
    return subtotals.map(s =>
      s.id === rangeEditSubtotalId
        ? { ...s, anchorDrawLineItemId: anchorId, anchorSerialNumber: newAnchorSerialNumber, range: checkedIds.size }
        : s,
    );
  }, [rangeEditSubtotalId, orderedIds, poolStartIndex, poolIds, activeItems, lineItems, anchorId, checkedIds, subtotals]);

  const confirmRangeEdit = () => {
    if (!rangeEditSubtotalId) return;

    const { reorderedLineItems, newAnchorSerialNumber } = computeReorderedLineItems(true);

    onApply({
      subtotalId: rangeEditSubtotalId,
      anchorDrawLineItemId: anchorId,
      anchorSerialNumber: newAnchorSerialNumber,
      range: checkedIds.size,
      reorderedLineItems,
    });

    cancelRangeEdit();
  };

  return {
    rangeEditSubtotalId,
    poolIds,
    checkedIds,
    anchorId,
    previewLineItems,
    previewSubtotals,
    startRangeEdit,
    toggleItem,
    cancelRangeEdit,
    confirmRangeEdit,
  };
}
